import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Eye, MousePointerClick, Target, Users, TrendingUp, Loader2, RefreshCw,
  BarChart3, Rocket,
} from "lucide-react";

const PERIODS = [
  { key: "7d",  label: "7 giorni" },
  { key: "30d", label: "30 giorni" },
  { key: "90d", label: "90 giorni" },
  { key: "all", label: "Sempre" },
];

// Map backend landing keys to display names
const LANDING_LABELS = {
  "home": "Homepage /",
  "dentista": "Dentista /dentista",
  "dentisti": "Dentisti /dentisti",
  "estetica": "Estetica /estetica",
  "hair": "Parrucchieri /hair",
  "benessere-posturale": "Fisio/Osteo /benessere-posturale",
  "fisioterapia": "Fisioterapia (legacy)",
  "barbiere": "Parrucchieri /hair (legacy)",
  "vertical": "Verticale (generico)",
  // Demo interattive (tracciate via /api/public/landing/track con landing=demo-*)
  "demo-odonto": "Demo Odonto /demo-odonto",
  "demo-benessere-posturale": "Demo Fisio /demo-benessere-posturale",
  "demo-estetica": "Demo Estetica /demo-estetica",
  "demo-hair": "Demo Hair /demo-hair",
  "demo-live": "Demo generica /demo-live",
  "demo": "Demo generica /demo",
};

// Canonical list of landings we always want to show in the table,
// even when they have 0 events in the selected period.
const KNOWN_LANDINGS = [
  "home",
  "dentista",
  "dentisti",
  "estetica",
  "hair",
  "benessere-posturale",
  "demo-odonto",
  "demo-benessere-posturale",
  "demo-estetica",
  "demo-hair",
];

const emptyRow = (landing) => ({
  landing,
  views: 0,
  unique_sessions: 0,
  cta_clicks: 0,
  lead_submits: 0,
  scroll_50: 0,
  scroll_90: 0,
  faq_opens: 0,
  roi_interactions: 0,
  conversion_rate: 0,
});

const KpiCard = ({ icon: Icon, label, value, sub, color = "from-sky-500 to-emerald-500", testid }) => (
  <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-md`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
    </div>
    <div className="text-3xl font-black text-slate-900 dark:text-white">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const LandingAnalyticsDashboard = () => {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (p = period) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/analytics/landing-analytics?period=${p}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

  const totals = data?.totals || {};
  const trend = data?.trend || [];

  // Merge backend landings with the canonical list so /hair /dentisti etc.
  // are always visible — even at 0 views in the selected period.
  const landings = useMemo(() => {
    const byKey = {};
    (data?.landings || []).forEach(l => { byKey[l.landing] = l; });
    KNOWN_LANDINGS.forEach(k => { if (!byKey[k]) byKey[k] = emptyRow(k); });
    // Include any extra keys the backend returned that aren't in KNOWN_LANDINGS
    return Object.values(byKey).sort((a, b) => (b.views || 0) - (a.views || 0));
  }, [data]);

  // Format the trend so days show as DD/MM for Italian users
  const trendFmt = useMemo(() => trend.map(t => ({
    ...t,
    day_label: t.day ? t.day.slice(8, 10) + "/" + t.day.slice(5, 7) : "",
  })), [trend]);

  const intFmt = (n) => typeof n === "number" ? n.toLocaleString("it-IT") : n;

  return (
    <div className="space-y-6" data-testid="landing-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shadow-md">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-serif font-medium">Landing Analytics</h2>
            <p className="text-sm text-muted-foreground">Tracciamento unificato di tutte le landing marketing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-full" data-testid="landing-period-selector">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  period === p.key
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                }`}
                data-testid={`landing-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline" size="sm" onClick={() => load(period)}
            className="rounded-full" disabled={loading}
            data-testid="landing-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento dati...
        </div>
      )}
      {error && !loading && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="landing-kpi-cards">
            <KpiCard icon={Eye}               label="Visualizzazioni" value={intFmt(totals.views || 0)}        sub="Totale page_view" color="from-sky-500 to-blue-600"       testid="kpi-views" />
            <KpiCard icon={Users}             label="Sessioni uniche" value={intFmt(totals.unique_sessions || 0)} sub="Utenti distinti"  color="from-emerald-500 to-teal-600" testid="kpi-sessions" />
            <KpiCard icon={MousePointerClick} label="Click CTA"       value={intFmt(totals.cta_clicks || 0)}   sub="Hero + Pricing + Demo" color="from-violet-500 to-purple-600" testid="kpi-cta" />
            <KpiCard icon={Target}            label="Lead inviati"    value={intFmt(totals.lead_submits || 0)} sub="Form compilati"   color="from-amber-500 to-orange-600" testid="kpi-leads" />
            <KpiCard icon={TrendingUp}        label="Conversion"      value={`${(totals.conversion_rate || 0).toFixed(1)}%`} sub="CTA / Views"    color="from-rose-500 to-pink-600"    testid="kpi-conversion" />
          </div>

          {/* Trend Chart */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm" data-testid="landing-trend-chart">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold">Andamento giornaliero</h3>
              </div>
              <span className="text-xs text-muted-foreground">
                {trendFmt.length > 0 ? `${trendFmt.length} giorni con attività` : "Nessun dato nel periodo"}
              </span>
            </div>
            {trendFmt.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendFmt}>
                    <defs>
                      <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradCTA" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                    <XAxis dataKey="day_label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="views"     name="Views"     stroke="#0ea5e9" strokeWidth={2} fill="url(#gradViews)" />
                    <Area type="monotone" dataKey="cta_clicks" name="Click CTA" stroke="#10b981" strokeWidth={2} fill="url(#gradCTA)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Nessun evento registrato nel periodo selezionato.
              </div>
            )}
          </div>

          {/* Per-Landing Table */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="landing-breakdown-table">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h3 className="font-semibold">Performance per landing</h3>
              <span className="text-xs text-muted-foreground">{landings.length} landing tracciate</span>
            </div>
            {landings.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Nessuna landing ha ricevuto eventi nel periodo selezionato.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold">Landing</th>
                      <th className="text-right px-3 py-3 font-semibold">Views</th>
                      <th className="text-right px-3 py-3 font-semibold">Sessioni</th>
                      <th className="text-right px-3 py-3 font-semibold">CTA</th>
                      <th className="text-right px-3 py-3 font-semibold">Leads</th>
                      <th className="text-right px-3 py-3 font-semibold">Scroll 50%</th>
                      <th className="text-right px-3 py-3 font-semibold">Scroll 90%</th>
                      <th className="text-right px-3 py-3 font-semibold">FAQ</th>
                      <th className="text-right px-5 py-3 font-semibold">Conv.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {landings.map((l, i) => {
                      const label = LANDING_LABELS[l.landing] || l.landing;
                      const conv = l.conversion_rate || 0;
                      const convColor = conv >= 20 ? "text-emerald-600" : conv >= 10 ? "text-amber-600" : "text-slate-500";
                      return (
                        <tr key={l.landing} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors" data-testid={`landing-row-${i}`}>
                          <td className="px-5 py-3 font-semibold">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500" />
                              {label}
                            </div>
                          </td>
                          <td className="text-right px-3 py-3 font-bold">{intFmt(l.views)}</td>
                          <td className="text-right px-3 py-3">{intFmt(l.unique_sessions)}</td>
                          <td className="text-right px-3 py-3 font-semibold text-violet-600">{intFmt(l.cta_clicks)}</td>
                          <td className="text-right px-3 py-3 font-semibold text-amber-600">{intFmt(l.lead_submits)}</td>
                          <td className="text-right px-3 py-3 text-slate-500">{intFmt(l.scroll_50)}</td>
                          <td className="text-right px-3 py-3 text-slate-500">{intFmt(l.scroll_90)}</td>
                          <td className="text-right px-3 py-3 text-slate-500">{intFmt(l.faq_opens)}</td>
                          <td className={`text-right px-5 py-3 font-black ${convColor}`}>{conv.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            I dati si aggiornano in tempo reale tramite <code className="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800">/api/public/landing/track</code>.
            Ogni visita e click viene tracciato anonimamente tramite session ID.
          </p>
        </>
      )}
    </div>
  );
};

export default LandingAnalyticsDashboard;
